import React, { useState } from 'react';
import styled from 'styled-components';
import OutbrainAds from '../components/OutbrainAds';

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--off-white);
`;

const HeroSection = styled.section`
  background: var(--carbon-gradient);
  padding: var(--space-16) 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="%23333" stroke-width="0.5" opacity="0.3"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.1;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-6);
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  color: var(--white);
  font-size: var(--text-5xl);
  font-weight: var(--font-black);
  margin-bottom: var(--space-4);
  background: linear-gradient(135deg, var(--white) 0%, var(--racing-red) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: var(--text-3xl);
  }
`;

const HeroSubtitle = styled.p`
  color: var(--chrome-silver);
  font-size: var(--text-xl);
  font-weight: var(--font-medium);
  max-width: 600px;
  margin: 0 auto;
  line-height: var(--leading-relaxed);
  
  @media (max-width: 768px) {
    font-size: var(--text-lg);
  }
`;

const QuizContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: var(--space-16) var(--space-6);
  
  @media (max-width: 768px) {
    padding: var(--space-12) var(--space-4);
  }
`;

const QuizCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-2xl);
  padding: var(--space-10);
  box-shadow: var(--shadow-xl);
  margin-bottom: var(--space-8);
  
  @media (max-width: 768px) {
    padding: var(--space-8);
  }
`;

const QuestionCounter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-8);
  padding: var(--space-4) var(--space-6);
  background: var(--light-gray);
  border-radius: var(--radius-xl);
`;

const CounterText = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--carbon-black);
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 8px;
  background: var(--light-gray);
  border-radius: var(--radius-full);
  margin: 0 var(--space-4);
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: var(--racing-gradient);
  border-radius: var(--radius-full);
  transition: width var(--transition-normal);
`;

const QuestionTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-3xl);
  font-weight: var(--font-black);
  color: var(--carbon-black);
  margin-bottom: var(--space-8);
  line-height: var(--leading-tight);
  
  @media (max-width: 768px) {
    font-size: var(--text-2xl);
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-8);
`;

const OptionButton = styled.button<{ isSelected: boolean; isCorrect?: boolean; isIncorrect?: boolean; showResult: boolean }>`
  padding: var(--space-4) var(--space-6);
  border: 2px solid ${props => {
    if (props.showResult && props.isCorrect) return 'var(--neon-green)';
    if (props.showResult && props.isIncorrect) return 'var(--racing-red)';
    if (props.isSelected) return 'var(--racing-red)';
    return 'var(--medium-gray)';
  }};
  background: ${props => {
    if (props.showResult && props.isCorrect) return 'var(--neon-green)';
    if (props.showResult && props.isIncorrect) return 'var(--racing-red)';
    if (props.isSelected) return 'var(--racing-red)';
    return 'var(--white)';
  }};
  color: ${props => {
    if (props.showResult && (props.isCorrect || props.isIncorrect)) return 'var(--white)';
    if (props.isSelected) return 'var(--white)';
    return 'var(--carbon-black)';
  }};
  border-radius: var(--radius-xl);
  font-family: 'Inter', sans-serif;
  font-size: var(--text-lg);
  font-weight: var(--font-medium);
  cursor: ${props => props.showResult ? 'default' : 'pointer'};
  transition: all var(--transition-fast);
  text-align: left;
  
  &:hover {
    ${props => !props.showResult && `
      border-color: var(--racing-red);
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
    `}
  }
  
  &:disabled {
    cursor: not-allowed;
  }
`;

const ActionButton = styled.button`
  background: var(--racing-gradient);
  color: var(--white);
  border: none;
  padding: var(--space-4) var(--space-8);
  border-radius: var(--radius-full);
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: var(--shadow-md);
  
  &:hover {
    background: var(--racing-red-dark);
    transform: translateY(-2px);
    box-shadow: var(--shadow-racing);
  }
  
  &:disabled {
    background: var(--medium-gray);
    cursor: not-allowed;
    transform: none;
    box-shadow: var(--shadow-sm);
  }
`;

const Explanation = styled.div`
  background: var(--light-gray);
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  margin-top: var(--space-6);
  border-left: 4px solid var(--electric-blue);
`;

const ExplanationText = styled.p`
  color: var(--dark-gray);
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  margin: 0;
`;

const ResultsCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-2xl);
  padding: var(--space-10);
  box-shadow: var(--shadow-xl);
  text-align: center;
`;

const ScoreDisplay = styled.div`
  margin-bottom: var(--space-8);
`;

const ScoreNumber = styled.div`
  font-size: var(--text-6xl);
  font-weight: var(--font-black);
  color: var(--racing-red);
  margin-bottom: var(--space-2);
  
  @media (max-width: 768px) {
    font-size: var(--text-4xl);
  }
`;

const ScoreText = styled.div`
  font-size: var(--text-xl);
  color: var(--carbon-black);
  font-weight: var(--font-semibold);
`;

const ScoreMessage = styled.p`
  font-size: var(--text-lg);
  color: var(--dark-gray);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-8);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--space-4);
  justify-content: center;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const QuizPage: React.FC = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);

  const questions: Question[] = [
    {
      id: 1,
      question: "Which company is known for the Model S electric sedan?",
      options: ["Ford", "Tesla", "BMW", "Audi"],
      correctAnswer: 1,
      explanation: "Tesla is famous for the Model S, one of the first luxury electric sedans that helped popularize electric vehicles."
    },
    {
      id: 2,
      question: "What does 'AWD' stand for in automotive terms?",
      options: ["All Weather Drive", "All Wheel Drive", "Advanced Wheel Design", "Automatic Wind Deflector"],
      correctAnswer: 1,
      explanation: "AWD stands for All Wheel Drive, a system that provides power to all four wheels for better traction and control."
    },
    {
      id: 3,
      question: "Which German luxury brand is known for its 'Ultimate Driving Machine' slogan?",
      options: ["Mercedes-Benz", "Audi", "BMW", "Porsche"],
      correctAnswer: 2,
      explanation: "BMW has used 'The Ultimate Driving Machine' as their slogan for decades, emphasizing their focus on driving performance."
    },
    {
      id: 4,
      question: "What type of engine does a hybrid car typically combine with an electric motor?",
      options: ["Diesel engine", "Gasoline engine", "Steam engine", "Jet engine"],
      correctAnswer: 1,
      explanation: "Hybrid cars typically combine a gasoline engine with an electric motor to improve fuel efficiency and reduce emissions."
    },
    {
      id: 5,
      question: "Which Italian brand is famous for supercars like the Huracán and Aventador?",
      options: ["Ferrari", "Lamborghini", "Maserati", "Alfa Romeo"],
      correctAnswer: 1,
      explanation: "Lamborghini is the Italian supercar manufacturer known for models like the Huracán and Aventador."
    },
    {
      id: 6,
      question: "What does 'MPG' measure in vehicles?",
      options: ["Miles Per Gallon", "Maximum Power Generated", "Motor Performance Grade", "Miles Per Gear"],
      correctAnswer: 0,
      explanation: "MPG stands for Miles Per Gallon, measuring how far a vehicle can travel on one gallon of fuel."
    },
    {
      id: 7,
      question: "Which Japanese brand is known for reliable vehicles like the Camry and Corolla?",
      options: ["Honda", "Nissan", "Toyota", "Mazda"],
      correctAnswer: 2,
      explanation: "Toyota is the Japanese automaker famous for reliable models like the Camry and Corolla."
    },
    {
      id: 8,
      question: "What does 'SUV' stand for?",
      options: ["Super Utility Vehicle", "Sport Utility Vehicle", "Standard Urban Vehicle", "Special Use Vehicle"],
      correctAnswer: 1,
      explanation: "SUV stands for Sport Utility Vehicle, combining passenger car comfort with truck-like capabilities."
    }
  ];

  const handleAnswerSelect = (answerIndex: number) => {
    if (showResult) return;
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;
    
    setShowResult(true);
    if (selectedAnswer === questions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setQuizCompleted(true);
    }
  };

  const handleRestartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setQuizCompleted(false);
  };

  const getScoreMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage >= 90) return "🏆 Automotive Expert! You know your cars inside and out!";
    if (percentage >= 70) return "🚗 Car Enthusiast! Great knowledge of automotive basics!";
    if (percentage >= 50) return "🔧 Getting There! You're building solid car knowledge!";
    return "📚 Keep Learning! There's always more to discover about cars!";
  };

  if (quizCompleted) {
    return (
      <PageContainer>
        <HeroSection>
          <HeroContent>
            <HeroTitle>Quiz Complete!</HeroTitle>
            <HeroSubtitle>
              How well do you know your automotive facts?
            </HeroSubtitle>
          </HeroContent>
        </HeroSection>

        <QuizContainer>
          <ResultsCard>
            <ScoreDisplay>
              <ScoreNumber>{score}/{questions.length}</ScoreNumber>
              <ScoreText>Questions Correct</ScoreText>
            </ScoreDisplay>
            
            <ScoreMessage>{getScoreMessage()}</ScoreMessage>
            
            <ButtonGroup>
              <ActionButton onClick={handleRestartQuiz}>
                Take Quiz Again
              </ActionButton>
              <ActionButton 
                onClick={() => window.location.href = '/'}
                style={{ background: 'var(--carbon-gradient)' }}
              >
                Back to Articles
              </ActionButton>
            </ButtonGroup>
          </ResultsCard>
          
          <OutbrainAds type="footer" />
          <OutbrainAds type="longscroll" />
        </QuizContainer>
      </PageContainer>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>CarBuzz Quiz</HeroTitle>
          <HeroSubtitle>
            Test your automotive knowledge with our fun car quiz!
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <QuizContainer>
        <QuizCard>
          <QuestionCounter>
            <CounterText>Question {currentQuestion + 1} of {questions.length}</CounterText>
            <ProgressBar>
              <ProgressFill progress={progress} />
            </ProgressBar>
            <CounterText>{Math.round(progress)}%</CounterText>
          </QuestionCounter>

          <QuestionTitle>{currentQ.question}</QuestionTitle>

          <OptionsContainer>
            {currentQ.options.map((option, index) => (
              <OptionButton
                key={index}
                isSelected={selectedAnswer === index}
                isCorrect={showResult && index === currentQ.correctAnswer}
                isIncorrect={showResult && selectedAnswer === index && index !== currentQ.correctAnswer}
                showResult={showResult}
                onClick={() => handleAnswerSelect(index)}
                disabled={showResult}
              >
                {String.fromCharCode(65 + index)}. {option}
              </OptionButton>
            ))}
          </OptionsContainer>

          {!showResult ? (
            <ActionButton 
              onClick={handleSubmitAnswer}
              disabled={selectedAnswer === null}
            >
              Submit Answer
            </ActionButton>
          ) : (
            <>
              <Explanation>
                <ExplanationText>{currentQ.explanation}</ExplanationText>
              </Explanation>
              <div style={{ marginTop: 'var(--space-6)' }}>
                <ActionButton onClick={handleNextQuestion}>
                  {currentQuestion < questions.length - 1 ? 'Next Question' : 'View Results'}
                </ActionButton>
              </div>
            </>
          )}
        </QuizCard>
        
        {/* Show ads immediately from first question */}
        <OutbrainAds type="footer" />
        <OutbrainAds type="longscroll" />
      </QuizContainer>
    </PageContainer>
  );
};

export default QuizPage;


